import {
  IngressPreEngagementData,
  LiveChatMetadata,
  buildLiveChatPreEngagementDataProps,
  LiveChatPreEngagementData,
  SghcLiveChatPreEngagementData,
  WiseOwlIngress,
} from "@twiliointernal/e2-commons-shared-definitions";
import {
  environmentsByZendeskRealmUrl,
  Realm,
  Realms,
  Config,
} from "./definitions";

const getConfig = () => {
  const env: Realm =
    environmentsByZendeskRealmUrl[window.location.host] || Realms.local;

  const config: Config = {
    prod: {
      zendeskTicketFormId: 360001809233,
      zendeskFieldIdSendgridUsername: 360027232094,
      zendeskFieldIdSendgridUserId: 360027567533,
      zendeskFieldIdSendgridMarketingPackage: 360027570713,
      zendeskFieldIdSendgridEmailPackage: 360027136013,
      zendeskFieldIdSendgridProduct: 360050708414,
      zendeskFieldIdPriorityLevel: 20551717,
    },
    stage: {
      zendeskTicketFormId: 360005394714,
      zendeskFieldIdSendgridUsername: 360039320654,
      zendeskFieldIdSendgridUserId: 360039306513,
      zendeskFieldIdSendgridMarketingPackage: 1260804540189,
      zendeskFieldIdSendgridEmailPackage: 1260804568230,
      zendeskFieldIdSendgridProduct: 360051782433,
      zendeskFieldIdPriorityLevel: 20551717,
    },
    dev: {
      zendeskTicketFormId: 360005394714,
      zendeskFieldIdSendgridUsername: 360039320654,
      zendeskFieldIdSendgridUserId: 360039306513,
      zendeskFieldIdSendgridMarketingPackage: 1260804540189,
      zendeskFieldIdSendgridEmailPackage: 1260804568230,
      zendeskFieldIdSendgridProduct: 360051782433,
      zendeskFieldIdPriorityLevel: 28245318,
    },
    local: {
      zendeskTicketFormId: 360005394714,
      zendeskFieldIdSendgridUsername: 360039320654,
      zendeskFieldIdSendgridUserId: 360039306513,
      zendeskFieldIdSendgridMarketingPackage: 1260804540189,
      zendeskFieldIdSendgridEmailPackage: 1260804568230,
      zendeskFieldIdSendgridProduct: 360051782433,
      zendeskFieldIdPriorityLevel: 28245318,
    },
  };

  return config[env];
};

const buildSendGridMetaData = (
  ingressPreEngagementData: IngressPreEngagementData,
  subProduct?: string
) => {
  const {
    zendeskFieldIdSendgridUsername,
    zendeskFieldIdSendgridUserId,
    zendeskFieldIdSendgridEmailPackage,
    zendeskFieldIdSendgridMarketingPackage,
    zendeskFieldIdSendgridProduct,
    zendeskFieldIdPriorityLevel,
  } = getConfig();

  return [
    {
      id: zendeskFieldIdSendgridUsername,
      value: ingressPreEngagementData?.email,
    },
    {
      id: zendeskFieldIdSendgridUserId,
      value: ingressPreEngagementData?.userSid,
    },
    {
      id: zendeskFieldIdSendgridEmailPackage,
      value: (window as any).EI_Plan,
    },
    {
      id: zendeskFieldIdSendgridMarketingPackage,
      value: (window as any).MC_Plan,
    },
    {
      id: zendeskFieldIdSendgridProduct,
      value: subProduct || "",
    },
    {
      id: zendeskFieldIdPriorityLevel,
      value: "p3_general_issue",
    },
  ];
};

const buildTwilioMetaData = (
  ingressPreEngagementData: IngressPreEngagementData
) => {
  return [
    {
      id: 28239607,
      value: ingressPreEngagementData?.accountSid,
    },
    {
      id: 28239617,
      value: ingressPreEngagementData?.userSid,
    },
    {
      id: 28245318,
      value: "p3_general_issue",
    },
  ];
};

const buildMetadata = (
  ingressPreEngagementData: IngressPreEngagementData,
  ingress: WiseOwlIngress,
  additionalMetadata?: LiveChatMetadata,
  subProduct?: string
) => {
  const { tags = [], hcaCsatProps } = additionalMetadata || {};
  let customFields;

  if (ingress === WiseOwlIngress.sendGrid) {
    customFields = buildSendGridMetaData(ingressPreEngagementData, subProduct);
  } else {
    customFields = buildTwilioMetaData(ingressPreEngagementData);
  }

  const metadata = {
    account: {
      sid: ingressPreEngagementData?.accountSid,
    },
    user: {
      sid: ingressPreEngagementData?.userSid,
      email: ingressPreEngagementData?.email,
      fullName: ingressPreEngagementData?.friendlyName,
    },
    customFields,
    tags: [...(tags ? tags : []), "sof_chat"],
    hcaCsatProps: hcaCsatProps ? hcaCsatProps : null,
  };

  return encodeURIComponent(JSON.stringify(metadata));
};

/**
 * Builds expected schema for live chat pre-engagement data
 * @param ingressPreEngagementData
 * @param productCategory
 * @param question
 * @returns stringified pre-engagement data
 */
export const buildLiveChatPreEngagementData = (
  args: buildLiveChatPreEngagementDataProps
) => {
  const {
    question,
    ingressPreEngagementData,
    productCategory,
    additionalMetadata,
    subProduct,
    username,
    ingress,
  } = args;
  console.log({ subProduct, ingress, productCategory });
  const preEngagementData:
    | LiveChatPreEngagementData
    | SghcLiveChatPreEngagementData = {
    question,
    friendlyName: ingressPreEngagementData?.friendlyName,
    email: ingressPreEngagementData?.email,
    productCategory,
    url: window.location.origin,
    userSid: ingressPreEngagementData?.userSid,
    accountSid: ingressPreEngagementData?.accountSid,
    identity: ingressPreEngagementData?.identity,
    metadata: buildMetadata(
      ingressPreEngagementData,
      ingress,
      additionalMetadata,
      subProduct
    ),
    location: window.location.origin,
    // Username and subProduct are only used for SGHC
    username,
    subProduct,
  };

  return JSON.stringify(preEngagementData);
};
